import { Observable, of, map, forkJoin, catchError } from 'rxjs';
import { FieldMergeConstants } from '@r1-compass-library/constants/field-merge.constants';
import { FileTransferService } from '../file-transfer.service';
import { inject, Injectable } from '@angular/core';
import {
    ImageMeta,
    FroalaElementWrapper,
    ImageAssetRef,
} from '@r1-compass-library/models/froala/froala-editor-options';

@Injectable({
    providedIn: 'root',
})
export class FroalaImagesService {
    readonly #fileTransferService = inject(FileTransferService);
    readonly #knownBlobUrls = new Set<string>();

    /**
     * ON LOAD: Replaces {{Image.fileName}} placeholders in database HTML with temporary
     * presigned GET URLs prior to populating the form control.
     */
    public hydrateImagePlaceholders(
        rawHtml: string,
        assetId: string,
        assetType: 'play' | 'content'
    ): Observable<string> {
        if (!rawHtml) {
            return of('');
        }

        const parser = new DOMParser();
        const doc = parser.parseFromString(rawHtml, 'text/html');

        //select all <img> tags and filter for placeholders
        const placeholderImages = Array.from(doc.querySelectorAll<HTMLImageElement>(FieldMergeConstants.IMG)).filter(
            img => {
                const src = img.getAttribute(FieldMergeConstants.SRC) || '';
                return src.includes(FieldMergeConstants.IMAGE_TOKEN_PREFIX);
            }
        );

        if (!placeholderImages.length) {
            return of(rawHtml);
        }

        const imageMap = new Map<string, HTMLImageElement[]>();
        placeholderImages.forEach(img => {
            const src = img.getAttribute(FieldMergeConstants.SRC) || '';
            const match = src.match(/\{\{Image\.(.*?)\}\}/);
            const fileName = (match ? match[1] : null) || this.extractFileNameFromElement(img);

            if (fileName) {
                if (!imageMap.has(fileName)) {
                    imageMap.set(fileName, []);
                }
                imageMap.get(fileName)!.push(img);
            }
        });

        if (imageMap.size === 0) {
            return of(rawHtml);
        }

        const requests$ = Array.from(imageMap.keys()).map(fileName => {
            return this.#fileTransferService.getSignedUrl(fileName, assetId, assetType, 'GET').pipe(
                map(res => ({ fileName, url: res?.url ?? '' })),
                catchError(() => of({ fileName, url: '' }))
            );
        });

        return forkJoin(requests$).pipe(
            map(results => {
                results.forEach(({ fileName, url }) => {
                    const targetImages = imageMap.get(fileName) || [];
                    targetImages.forEach(img => {
                        img.setAttribute(
                            FieldMergeConstants.DATA_CONTENT_OPTIONS,
                            JSON.stringify({ fileName, assetId, assetType })
                        );
                        img.classList.add(FieldMergeConstants.IMAGE_ASSET);
                        img.setAttribute('crossorigin', 'anonymous'); // needed for CORS s3
                        if (!img.classList.contains(FieldMergeConstants.FR_FIC)) {
                            img.classList.add(FieldMergeConstants.FR_FIC, FieldMergeConstants.FR_DIB);
                        }

                        if (url) {
                            img.setAttribute(FieldMergeConstants.SRC, url);
                        } else {
                            // Fallback if presigned GET endpoint call failed
                            img.classList.add(FieldMergeConstants.FR_IMAGE_ERROR);
                            img.setAttribute(FieldMergeConstants.FR_DATA_HAS_IMAGE_ERROR, 'true');
                            img.setAttribute(FieldMergeConstants.SRC, this.buildErrorImageSvgDataUrl(fileName));
                        }
                    });
                });

                return doc.body.innerHTML;
            })
        );
    }

    /**
     * ON SAVE: Converts <img> tags back into {{Image.fileName}} placeholder tokens
     * before persisting form data.
     */
    public normalizeHtmlForSave(rawHtml: string): string {
        if (!rawHtml) {
            return '';
        }

        const parser = new DOMParser();
        const doc = parser.parseFromString(rawHtml, 'text/html');
        const selector = FieldMergeConstants.IMAGE_ASSET_WITH_DATA_OPTIONS_SELECTOR;

        doc.querySelectorAll<HTMLImageElement>(selector).forEach(img => {
            img.removeAttribute(FieldMergeConstants.COPY_SRC);
            img.removeAttribute('data-uploading');
            const fileName = this.extractFileNameFromElement(img);
            if (fileName) {
                img.setAttribute(
                    FieldMergeConstants.SRC,
                    `${FieldMergeConstants.IMAGE_TOKEN_PREFIX}${fileName}${FieldMergeConstants.TOKEN_SUFFIX}`
                );
                img.classList.remove(FieldMergeConstants.FR_IMAGE_ERROR);
                img.removeAttribute(FieldMergeConstants.FR_DATA_HAS_IMAGE_ERROR);
            }
        });

        return doc.body.innerHTML;
    }

    public extractFileNameFromElement(img: Element | HTMLImageElement | null | undefined): string | null {
        if (!img) {
            return null;
        }
        const meta = this.readImageMeta(img);
        if (meta) {
            return meta.fileName;
        }
        const alt = img.getAttribute('alt');
        if (alt && alt !== 'Unknown Image' && !alt.startsWith('data:') && !alt.startsWith('http')) {
            return alt;
        }
        return null;
    }

    public getUsedImages = (document: Document): Set<string> => {
        const imageNames = new Set<string>();
        const selector = FieldMergeConstants.IMAGE_ASSET_WITH_DATA_OPTIONS_SELECTOR;
        const imageElements = document
            .querySelector(`.${FieldMergeConstants.FR_ELEMENT}`)
            ?.querySelectorAll<HTMLImageElement>(selector);

        imageElements?.forEach(img => {
            const options = img.getAttribute(FieldMergeConstants.DATA_CONTENT_OPTIONS);
            if (options) {
                try {
                    const parsed = JSON.parse(options);
                    if (parsed?.fileName) {
                        imageNames.add(parsed.fileName);
                    }
                } catch {
                    // Ignore malformed attributes
                }
            }
        });

        return imageNames;
    };

    buildImageElementHtml(url: string, meta: ImageMeta): string {
        const safeAlt = meta.fileName.replace(/"/g, '&quot;');
        const contentOptions = JSON.stringify(meta).replace(/'/g, '&#39;');
        return `<img src="${url}" class="${FieldMergeConstants.IMAGE_ASSET} ${FieldMergeConstants.FR_FIC} ${FieldMergeConstants.FR_DIB}" ${FieldMergeConstants.DATA_CONTENT_OPTIONS}='${contentOptions}' alt="${safeAlt}" />`;
    }

    /**
     * Generates an SVG Data URI vector badge for missing/broken images.
     */
    buildErrorImageSvgDataUrl(fileName: string): string {
        const safeFileName = fileName.replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="320" height="70" viewBox="0 0 320 70">
            <rect width="100%" height="100%" fill="#FDEDEC" rx="6" stroke="#E74C3C" stroke-width="2" stroke-dasharray="4 4"/>
            <g transform="translate(15, 20)">
                <path d="M12 0 L24 22 L0 22 Z" fill="#E74C3C"/>
                <text x="12" y="18" font-family="sans-serif" font-weight="bold" font-size="14" fill="#FFFFFF" text-anchor="middle">!</text>
            </g>
            <text x="50" y="32" font-family="sans-serif" font-size="12" font-weight="bold" fill="#C0392B">Missing Image Asset</text>
            <text x="50" y="48" font-family="sans-serif" font-size="11" fill="#7F8C8D">${safeFileName}</text>
        </svg>`;
        return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    }

    /**
     * Generates an SVG image for use as a placeholder while an image is being copied.
     */
    buildLoadingImageSvgDataUrl(fileName: string): string {
        const safeFileName = fileName.replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="320" height="70" viewBox="0 0 320 70">
            <rect width="100%" height="100%" fill="#F4F6F7" rx="6" stroke="#95A5A6" stroke-width="2" stroke-dasharray="4 4"/>
            <text x="20" y="32" font-family="sans-serif" font-size="12" font-weight="bold" fill="#566573">Copying image…</text>
            <text x="20" y="48" font-family="sans-serif" font-size="11" fill="#7F8C8D">${safeFileName}</text>
        </svg>`;
        return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    }

    /**
     * Attaches a capture-phase error listener to the editor container to intercept broken image loads.
     */
    addImageErrorCaptureListener(root: ParentNode = document): void {
        const dropTargets = root.querySelectorAll('[data-role="drag-drop-target"] div.fr-view');
        const targets = dropTargets.length ? dropTargets : [root];

        targets.forEach(target => {
            target.addEventListener(
                'error',
                (event: Event) => {
                    const img = event.target as HTMLElement | null;
                    if (
                        img instanceof HTMLImageElement &&
                        (img.classList.contains(FieldMergeConstants.IMAGE_ASSET) ||
                            img.hasAttribute(FieldMergeConstants.DATA_CONTENT_OPTIONS))
                    ) {
                        if (img.dataset['hasImageError'] === 'true') {
                            return;
                        }

                        img.dataset['hasImageError'] = 'true';
                        img.classList.add('fr-image-error');

                        const meta = this.readImageMeta(img);
                        let fileName = img.getAttribute('alt') || 'Unknown Image';
                        if (meta?.fileName) {
                            fileName = meta.fileName;
                        }

                        img.src = this.buildErrorImageSvgDataUrl(fileName);
                    }
                },
                true // Capture phase required as HTMLImageElement 'error' events do not bubble
            );
        });
    }

    /**
     * Resolves an HTMLImageElement from various Froala image wrapper structures (native element, jQuery-like wrapper, etc.)
     */
    resolveImageElement(imgTarget: unknown): HTMLImageElement | null {
        if (!imgTarget) {
            return null;
        }
        if (imgTarget instanceof HTMLImageElement) {
            return imgTarget;
        }
        if (
            typeof imgTarget === 'object' &&
            imgTarget !== null &&
            (imgTarget as Record<number, unknown>)[0] instanceof HTMLImageElement
        ) {
            return (imgTarget as Record<number, unknown>)[0] as HTMLImageElement;
        }
        return null;
    }

    /**
     * Updates an existing Froala-pasted temporary <img> element in-place with the local blob URL,
     * alt text, and field-merge tracking attributes. Returns true if an element was found and updated.
     */
    updatePastedImage(imgTarget: unknown, localBlobUrl: string, meta: ImageMeta): boolean {
        const rawEl = this.resolveImageElement(imgTarget);

        if (!rawEl) {
            return false;
        }

        const contentOptions = JSON.stringify(meta);
        rawEl.src = localBlobUrl;
        rawEl.setAttribute(FieldMergeConstants.DATA_CONTENT_OPTIONS, contentOptions);
        rawEl.setAttribute('alt', meta.fileName);
        rawEl.classList.add(FieldMergeConstants.IMAGE_ASSET, FieldMergeConstants.FR_FIC, FieldMergeConstants.FR_DIB);
        rawEl.removeAttribute(FieldMergeConstants.FR_PASTED_IMAGE);
        rawEl.removeAttribute('data-uploading');

        return true;
    }

    removePastedImage(imgTarget: unknown): void {
        if (!imgTarget) {
            return;
        }

        if (imgTarget instanceof Element) {
            imgTarget.remove();
            return;
        }

        const target = imgTarget as FroalaElementWrapper;
        if (typeof target.remove === 'function') {
            target.remove();
        } else if (typeof target === 'object' && target[0] instanceof Element) {
            target[0].remove();
        }
    }

    resolveImageFileName(file: File | Blob, fallbackName?: string | null): string {
        const ext = this.getExtensionFromMimeType(file.type);
        if (
            fallbackName &&
            fallbackName !== 'image.png' &&
            fallbackName !== 'blob' &&
            fallbackName !== 'Unknown Image'
        ) {
            return this.#toSafeFileName(fallbackName, ext);
        }
        const name = (file as File).name;
        if (!name || name === 'image.png' || name === 'blob') {
            return `pasted-image-${Date.now()}.${ext}`;
        }
        return this.#toSafeFileName(name, ext);
    }

    trackBlobUrl(url: string): void {
        if (url) {
            this.#knownBlobUrls.add(url);
        }
    }

    isKnownBlobUrl(url: string): boolean {
        return Boolean(url && this.#knownBlobUrls.has(url));
    }

    getExtensionFromMimeType(mimeType: string): string {
        if (!mimeType) {
            return 'png';
        }
        const subtype = mimeType.split('/')[1]?.toLowerCase() || 'png';
        if (subtype.includes('jpeg') || subtype.includes('jpg')) {
            return 'jpg';
        }
        if (subtype.includes('png')) {
            return 'png';
        }
        if (subtype.includes('gif')) {
            return 'gif';
        }
        if (subtype.includes('webp')) {
            return 'webp';
        }
        if (subtype.includes('svg')) {
            return 'svg';
        }
        return subtype.split('+')[0] || 'png';
    }

    readImageMeta(img: Element | null | undefined): ImageMeta | null {
        const raw = img?.getAttribute(FieldMergeConstants.DATA_CONTENT_OPTIONS);
        if (!raw) return null;
        try {
            const parsed = JSON.parse(raw);
            return parsed?.fileName ? (parsed as ImageMeta) : null;
        } catch {
            return null;
        }
    }

    /** Writes the metadata and puts back the classes that Froala's pasteDeniedAttrs (['class','id']) strips on paste. */
    writeImageMeta(img: HTMLImageElement, meta: ImageMeta): void {
        img.setAttribute(FieldMergeConstants.DATA_CONTENT_OPTIONS, JSON.stringify(meta));
        img.setAttribute('alt', meta.fileName);
        img.classList.add(FieldMergeConstants.IMAGE_ASSET, FieldMergeConstants.FR_FIC, FieldMergeConstants.FR_DIB);
    }

    /** Use only for images copied by code that predates assetId in data-content-options. */
    parseAssetRefFromS3Url(src: string): ImageAssetRef | null {
        // .../compassrefresh/RSD/images/plays/11493/cows.jpg
        const m = src.match(/\/images\/(plays|contentassets)\/([^/]+)\//); // .../images/contentassets/1/pasted-image-123.jpg
        if (!m) {
            return null;
        }
        return { assetId: m[2], assetType: m[1] === 'plays' ? 'play' : 'content' }; // only used for imgs copied before this change
    }

    /** Builds a name that won't overwrite a different image already used in this asset. */
    uniqueFileName(fileName: string, taken: Set<string>): string {
        if (!taken.has(fileName)) {
            return fileName;
        }
        const dot = fileName.lastIndexOf('.');
        const base = dot > 0 ? fileName.slice(0, dot) : fileName;
        const ext = dot > 0 ? fileName.slice(dot) : '';
        let i = 1;
        while (taken.has(`${base}-${i}${ext}`)) {
            i++;
        }
        return `${base}-${i}${ext}`;
    }

    #toSafeFileName(name: string, ext: string): string {
        const dot = name.lastIndexOf('.');
        const hasExt = dot > 0 && /^[A-Za-z0-9]{2,5}$/.test(name.slice(dot + 1));
        const base = (hasExt ? name.slice(0, dot) : name)
            .replace(/[^A-Za-z0-9._-]+/g, '-')
            .replace(/^[-.]+|[-.]+$/g, '')
            .slice(0, 100);
        if (!base) {
            return `pasted-image-${Date.now()}.${ext}`;
        }
        return `${base}.${hasExt ? name.slice(dot + 1) : ext}`;
    }
}
